import { v4 as uuidv4 } from 'uuid';

import type { ConditionGroup, ConditionLeaf, ConditionNode, LogicalOp } from '../types/rule';

/**
 * ✅ FUNÇÃO PARA AGRUPAR CONDIÇÕES RECURSIVAMENTE 2 A 2
 *
 * Recebe uma lista flat de condições (primeiro child de um ConditionGroup)
 * e transforma em estrutura hierárquica aninhada.
 *
 * Lógica:
 * - Primeira condição vira o "núcleo"
 * - Cada condição seguinte agrupa com o anterior baseado no logicalOperator
 * - Agrupamento 2 a 2: ((((C1 AND C2) OR C3) AND C4) OR C5)
 *
 * Exemplo:
 * Input: [
 *   { id: '1', left: 'submission.value', operator: 'EQUALS', right: 'A' },
 *   { id: '2', left: 'submission.value', operator: 'EQUALS', right: 'B', logicalOperator: 'AND' },
 *   { id: '3', left: 'submission.value', operator: 'EQUALS', right: 'C', logicalOperator: 'OR' },
 * ]
 *
 * Output: {
 *   type: 'group',
 *   logical: 'OR',  // Último operador
 *   children: [
 *     {
 *       type: 'group',
 *       logical: 'AND',  // Primeiro operador
 *       children: [
 *         { ...C1 },
 *         { ...C2 }
 *       ]
 *     },
 *     { ...C3 }
 *   ]
 * }
 *
 * Estrutura final: ((C1 AND C2) OR C3)
 */
export function buildConditionHierarchy(conditions: ConditionLeaf[]): ConditionGroup {
  console.log('🏗️ === buildConditionHierarchy ===');
  console.log('📥 Input (flat list):', conditions);

  if (conditions.length === 0) {
    // Retornar grupo vazio
    return {
      id: uuidv4(),
      type: 'group',
      logical: 'AND',
      children: [],
    };
  }

  if (conditions.length === 1) {
    // Apenas 1 condição: retornar grupo simples
    console.log('✅ Apenas 1 condição, retornando grupo simples');
    return {
      id: uuidv4(),
      type: 'group',
      logical: 'AND',
      children: [conditions[0]],
    };
  }

  // ✅ ALGORITMO DE AGRUPAMENTO 2 A 2
  // Começamos com a primeira condição
  let currentNode: ConditionNode = conditions[0];

  // Processar condições restantes (da segunda em diante)
  for (let i = 1; i < conditions.length; i++) {
    const condition = conditions[i];
    const logicalOp = condition.logicalOperator || 'AND'; // Default: AND

    console.log(`🔗 Agrupando condição ${i + 1} com operador: ${logicalOp}`);

    // Criar novo grupo que envolve o nó atual + nova condição
    const newGroup: ConditionGroup = {
      id: uuidv4(),
      type: 'group',
      logical: logicalOp,
      children: [
        currentNode, // O que já tínhamos (pode ser uma folha ou um grupo anterior)
        condition, // Nova condição
      ],
    };

    // O novo grupo se torna o nó atual
    currentNode = newGroup;

    console.log(`✅ Grupo ${i} criado:`, {
      logical: logicalOp,
      childrenTypes: newGroup.children.map(c => c.type),
    });
  }

  // ✅ O resultado final é o último grupo criado
  // Mas precisamos envolver em um grupo raiz para manter compatibilidade
  const finalGroup: ConditionGroup = {
    id: uuidv4(),
    type: 'group',
    logical: (currentNode as ConditionGroup).logical || 'AND',
    children: [currentNode],
  };

  console.log('🎯 Output (hierarquia):', finalGroup);
  console.log('🏁 === FIM buildConditionHierarchy ===');

  return finalGroup;
}

/**
 * ✅ FUNÇÃO AUXILIAR: Visualizar hierarquia como string
 * Para debug e testes
 */
export function visualizeHierarchy(node: ConditionNode, indent = 0): string {
  const spaces = '  '.repeat(indent);

  if (node.type === 'rule') {
    return `${spaces}LEAF: ${node.left} ${node.operator} ${node.right}\n`;
  }

  const group = node as ConditionGroup;
  let result = `${spaces}GROUP (${group.logical}):\n`;

  for (const child of group.children) {
    result += visualizeHierarchy(child, indent + 1);
  }

  return result;
}

/**
 * ✅ FUNÇÃO INVERSA: Converter hierarquia em lista flat com logicalOperators
 *
 * Recebe uma estrutura hier árquica (da edição/backend) e retorna lista flat
 * com os logicalOperators corretos para cada condição.
 *
 * Lógica:
 * - Primeira condição: sem logicalOperator
 * - Demais: logicalOperator vem do grupo pai
 *
 * Exemplo:
 * Input: GROUP(OR) [
 *   GROUP(AND) [C1, C2],
 *   C3
 * ]
 *
 * Output: [
 *   C1,                             // Primeira: sem logicalOperator
 *   C2 {logicalOperator: 'AND'},   // Vem do grupo GROUP(AND)
 *   C3 {logicalOperator: 'OR'}     // Vem do grupo GROUP(OR)
 * ]
 */
export function flattenHierarchy(root: ConditionGroup): ConditionLeaf[] {
  console.log('🔽 === flattenHierarchy (REESCRITO) ===');
  console.log('📥 Input (hierarquia):', JSON.stringify(root, null, 2));

  const result: ConditionLeaf[] = [];
  const operators: LogicalOp[] = []; // Rastrear operadores na ordem de criação

  /**
   * ✅ ALGORITMO REVERSO DO AGRUPAMENTO 2 a 2
   *
   * Padrão do backend: GROUP(OP_ÚLTIMO)[GROUP(OP_ANTERIOR)[...], LEAF_ÚLTIMA]
   *
   * Estratégia:
   * 1. Descer pela "esquerda" (primeiro filho) até encontrar as folhas iniciais
   * 2. Anotar os operadores encontrados pelo caminho
   * 3. Aplicar operadores na ordem REVERSA
   */
  function extractInOrder(node: ConditionNode): void {
    if (node.type === 'rule') {
      // Folha encontrada - adicionar ao resultado
      result.push(node as ConditionLeaf);
      return;
    }

    // É um grupo
    const group = node as ConditionGroup;

    if (group.children.length === 2) {
      const [left, right] = group.children;

      // ✅ Processar filho ESQUERDO primeiro (pode conter mais grupos)
      extractInOrder(left);

      // ✅ Anotar operador deste grupo
      operators.push(group.logical);

      // ✅ Processar filho DIREITO (geralmente é a última folha)
      extractInOrder(right);
    } else if (group.children.length === 1) {
      // Grupo wrapper - descer
      extractInOrder(group.children[0]);
    } else {
      // Múltiplos filhos (não deveria acontecer em 2 a 2)
      for (const child of group.children) {
        extractInOrder(child);
      }
    }
  }

  // Descer até o nó mais profundo
  let currentNode: ConditionNode = root;
  while (currentNode.type === 'group' && currentNode.children.length === 1) {
    currentNode = (currentNode as ConditionGroup).children[0];
  }

  extractInOrder(currentNode);

  // ✅ Aplicar operadores às condições (exceto a primeira)
  console.log('📊 Operadores extraídos:', operators);
  console.log('📊 Condições extraídas:', result.length);

  for (let i = 1; i < result.length && i - 1 < operators.length; i++) {
    result[i] = {
      ...result[i],
      logicalOperator: operators[i - 1],
    };
  }

  console.log(
    '📤 Output (flat list):',
    result.map((c, i) => ({
      index: i + 1,
      field: c.meta?.field_label || '?',
      operator: c.logicalOperator || 'NONE',
    }))
  );
  console.log('🏁 === FIM flattenHierarchy ===');

  return result;
}

/**
 * ✅ TESTE UNITÁRIO (executar no console)
 */
export function testHierarchy() {
  console.log('🧪 === TESTE DE HIERARQUIA ===');

  // Cenário 1: 2 condições com AND
  const test1: ConditionLeaf[] = [
    {
      id: '1',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'A',
    },
    {
      id: '2',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'B',
      logicalOperator: 'AND',
    },
  ];

  const result1 = buildConditionHierarchy(test1);
  console.log('📊 Cenário 1 (2 condições AND):');
  console.log(visualizeHierarchy(result1));

  // Cenário 2: 3 condições (AND, OR)
  const test2: ConditionLeaf[] = [
    {
      id: '1',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'A',
    },
    {
      id: '2',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'B',
      logicalOperator: 'AND',
    },
    {
      id: '3',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'C',
      logicalOperator: 'OR',
    },
  ];

  const result2 = buildConditionHierarchy(test2);
  console.log('📊 Cenário 2 (3 condições: AND, OR):');
  console.log(visualizeHierarchy(result2));

  // Cenário 3: 5 condições (mix)
  const test3: ConditionLeaf[] = [
    { id: '1', type: 'rule', left: 'submission.value', operator: 'EQUALS', right: 'A' },
    {
      id: '2',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'B',
      logicalOperator: 'AND',
    },
    {
      id: '3',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'C',
      logicalOperator: 'OR',
    },
    {
      id: '4',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'D',
      logicalOperator: 'AND',
    },
    {
      id: '5',
      type: 'rule',
      left: 'submission.value',
      operator: 'EQUALS',
      right: 'E',
      logicalOperator: 'OR',
    },
  ];

  const result3 = buildConditionHierarchy(test3);
  console.log('📊 Cenário 3 (5 condições: AND, OR, AND, OR):');
  console.log(visualizeHierarchy(result3));

  console.log('✅ === FIM TESTES ===');
}
